import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import { caseActions } from "@_actions/case.actions";
import { stateError, isComponentDisabled, catchError } from "@_utils/helpers";
import MenuItem from "@mui/material/MenuItem";
import styles from "./SelectEmailFlowPopup.module.css";
import { useSnackbar } from "@_hooks/useSnackbar";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";

const componetTag = "select_email_flow_popup";
function SelectEmailFlowPopup(props) {
  const dispatch = useDispatch();
  const { caseId, data, close, open } = props;
  const caseReducer = useSelector((state) => {
    return state.caseReducer;
  });
  const emailFlowReducer = useSelector((state) => {
    return state.emailFlowReducer;
  });
  const addFlowStatus = caseReducer.status.CASE_ADD_FLOW;
  const emailFlows = emailFlowReducer.emailFlows || [];
  const inputDisabled = isComponentDisabled(componetTag, data.state);
  const flowCount = emailFlows.length;
  const onlyOneAvailableFlowId = flowCount === 1 ? emailFlows[0]?.id : "";
  const isAddFlowInProgress = addFlowStatus === "request";

  const [flow, setFlow] = useState(data.email_flow && data.email_flow.id);
  const { showSnackbarError, snackbarProps } = useSnackbar();

  const closePopup = useCallback(() => {
    dispatch(caseActions.clearStateMessage("CASE_ADD_FLOW"));
    close();
  }, [dispatch, close]);

  function actionClicked() {
    if (inputDisabled || !flow || isAddFlowInProgress) {
      return;
    }

    dispatch(caseActions.addCaseFlow(caseId, flow));
  }

  /**
   * Handle add-flow result.
   * Why: gets case details after flow assignment, clear status, close popup.
   * When: runs on `CASE_ADD_FLOW` status changes.
   */
  useEffect(() => {
    if (addFlowStatus === "success") {
      dispatch(caseActions.getCaseWithChargeDetails(caseId, data.overview));
      closePopup();
    } else if (catchError(addFlowStatus)) {
      showSnackbarError(stateError(addFlowStatus));
      dispatch(caseActions.clearStateMessage("CASE_ADD_FLOW"));
    }
  }, [
    addFlowStatus,
    caseId,
    data.overview,
    showSnackbarError,
    closePopup,
    dispatch,
  ]);

  function handleInputChange(e) {
    setFlow(e.target.value);
  }

  /**
   * Sync flow selection when popup opens or source data changes.
   * Why: avoids stale selection between popup openings and keeps state aligned.
   * Behavior:
   * - if assigned flow exists, select it
   * - else if exactly one available flow, preselect it
   * - else clear selection
   */
  useEffect(() => {
    if (!open) {
      return;
    }
    if (data.email_flow?.id) {
      setFlow(data.email_flow.id);
    } else if (flowCount === 1) {
      setFlow(onlyOneAvailableFlowId);
    } else {
      setFlow("");
    }
  }, [open, data.email_flow?.id, flowCount, onlyOneAvailableFlowId]);

  return (
    <DialogPopup title="Assign mail flow" open={open} onClose={closePopup}>
      <div className={styles.content_container}>
        <TextfieldInput
          select
          label="Mail Flow"
          id="flow"
          name="flow"
          value={flow ?? ""}
          onChange={handleInputChange}
          disabled={inputDisabled}
        >
          {emailFlows.map((email) => (
            <MenuItem key={email.id} value={email.id}>
              {email.name}
            </MenuItem>
          ))}
        </TextfieldInput>
      </div>
      <div className="action_container">
        {!inputDisabled ? (
          <>
            <Button
              className="btn_cancel"
              onClick={closePopup}
              data-testid="select-email-flow-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="btn_confirm"
              onClick={actionClicked}
              disabled={inputDisabled || !flow || isAddFlowInProgress}
              data-testid="select-email-flow-confirm-btn"
            >
              Confirm
            </Button>
          </>
        ) : (
          <Button
            className="btn_cancel"
            onClick={closePopup}
            data-testid="select-email-flow-close-btn"
          >
            Close
          </Button>
        )}
      </div>
      <AppSnackbar {...snackbarProps} />
    </DialogPopup>
  );
}

export default SelectEmailFlowPopup;
